import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Skeleton,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { useAuth } from 'Hooks/useAuth';
import { useResponsive } from 'Hooks/useResponsive';
import { Recipe } from 'Types/api';
import {
  getDailySuggestion,
  getTodayDateString,
  getSkipCount,
  incrementSkipCount,
} from 'Utils/suggestionUtils';

interface TodaysRecipeProps {
  recipes: Recipe[];
  isLoading?: boolean;
}

const TodaysRecipe = ({ recipes, isLoading = false }: TodaysRecipeProps) => {
  const navigate = useNavigate();
  const { userId } = useAuth();
  const { isMobile } = useResponsive();
  const [skipCount, setSkipCount] = useState(0);
  const [suggestedRecipe, setSuggestedRecipe] = useState<Recipe | null>(null);

  // Initialize skip count from localStorage on mount
  useEffect(() => {
    setSkipCount(getSkipCount());
  }, []);

  // Update suggested recipe when recipes or skipCount changes
  useEffect(() => {
    if (recipes.length > 0 && userId) {
      const today = getTodayDateString();
      const suggestion = getDailySuggestion(recipes, userId, today, skipCount);
      setSuggestedRecipe(suggestion);
    } else {
      setSuggestedRecipe(null);
    }
  }, [recipes, userId, skipCount]);

  const handleTryAnother = useCallback(() => {
    const newCount = incrementSkipCount();
    setSkipCount(newCount);
  }, []);

  const handleViewRecipe = useCallback(() => {
    if (suggestedRecipe) {
      navigate(`/recipes/${suggestedRecipe.id}`);
    }
  }, [navigate, suggestedRecipe]);

  const handleEditRecipe = useCallback(() => {
    if (suggestedRecipe) {
      navigate(`/recipes/${suggestedRecipe.id}/edit`);
    }
  }, [navigate, suggestedRecipe]);

  const isDraft = suggestedRecipe?.status === 'draft';

  // Loading state
  if (isLoading) {
    return <TodaysRecipeSkeleton isMobile={isMobile} />;
  }

  // Empty state - no recipes
  if (recipes.length === 0) {
    return null; // Don't show the component if there are no recipes
  }

  // No suggestion available
  if (!suggestedRecipe) {
    return null;
  }

  const totalTime = suggestedRecipe.prepTime + suggestedRecipe.cookTime;

  return (
    <Card
      variant='outlined'
      sx={{
        mb: 3,
        bgcolor: isDraft ? 'warning.50' : 'primary.50',
        borderColor: isDraft ? 'warning.200' : 'primary.200',
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography
          variant='subtitle2'
          color={isDraft ? 'warning.main' : 'primary.main'}
          sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {isDraft ? 'Finish Your Recipe' : "Today's Recipe Suggestion"}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
          }}
        >
          {/* Recipe Image */}
          {suggestedRecipe.imageUrl ? (
            <CardMedia
              component='img'
              image={suggestedRecipe.imageUrl}
              alt={suggestedRecipe.title}
              sx={{
                width: isMobile ? '100%' : 180,
                height: isMobile ? 160 : 140,
                borderRadius: 1,
                objectFit: 'cover',
                flexShrink: 0,
              }}
            />
          ) : (
            <Box
              sx={{
                width: isMobile ? '100%' : 180,
                height: isMobile ? 160 : 140,
                borderRadius: 1,
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <RestaurantIcon sx={{ fontSize: 48, color: 'grey.400' }} />
            </Box>
          )}

          {/* Recipe Details */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant='h6' component='h3' gutterBottom noWrap>
              {suggestedRecipe.title}
            </Typography>

            <Typography
              variant='body2'
              color='text.secondary'
              sx={{
                mb: 2,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {suggestedRecipe.description}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {isDraft && <Chip label='Draft' size='small' color='warning' />}
              <Chip
                icon={<AccessTimeIcon />}
                label={`${totalTime} min`}
                size='small'
                variant='outlined'
              />
              <Chip
                icon={<RestaurantIcon />}
                label={`${suggestedRecipe.servings} servings`}
                size='small'
                variant='outlined'
              />
              <Chip
                label={suggestedRecipe.category}
                size='small'
                color='primary'
                variant='outlined'
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {isDraft ? (
                <Button
                  variant='contained'
                  size='small'
                  color='warning'
                  startIcon={<EditIcon />}
                  onClick={handleEditRecipe}
                >
                  Continue Editing
                </Button>
              ) : (
                <Button variant='contained' size='small' onClick={handleViewRecipe}>
                  View Recipe
                </Button>
              )}
              <Button
                variant='outlined'
                size='small'
                startIcon={<RefreshIcon />}
                onClick={handleTryAnother}
              >
                Try Another
              </Button>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const TodaysRecipeSkeleton = ({ isMobile }: { isMobile: boolean }) => (
  <Card variant='outlined' sx={{ mb: 3 }}>
    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
      <Skeleton variant='text' width={200} height={20} sx={{ mb: 2 }} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 2,
        }}
      >
        <Skeleton
          variant='rounded'
          sx={{
            width: isMobile ? '100%' : 180,
            height: isMobile ? 160 : 140,
            flexShrink: 0,
          }}
        />
        <Box sx={{ flexGrow: 1 }}>
          <Skeleton variant='text' width='60%' height={32} />
          <Skeleton variant='text' width='100%' />
          <Skeleton variant='text' width='80%' sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant='rounded' width={80} height={24} />
            <Skeleton variant='rounded' width={100} height={24} />
          </Box>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export default TodaysRecipe;
